import { useParams, useNavigate, Outlet } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import VersionLesson from './VersionLesson';
import VersionProgress from './VersionProgress';
import VersionSuccess from './VersionSuccess';
import arrowLeft from '../images/arrow-left.svg';
import diploma from '../images/diploma.svg';
import { VersionItem, DetailedLesson, VersionAccountStatus, ProjectCore } from '../types';
import ErrorPage from './ErrorPage';
import { useApiService } from '@/hooks/useApiService';

import { TailSpin } from 'react-loader-spinner';

function Version() {
    const { getVersion, getVersionAccountStatus, getProjectInfos, checkLessonStatus } = useApiService();
    const { formationId, versionId, lessonId } = useParams();
    const navigate = useNavigate();

    const [version, setVersion] = useState<VersionItem>();
    const [displayedLessonId, setDisplayedLessonId] = useState<number | undefined>(() => {
        if (lessonId === 'success') {
            return undefined;
        }
        const parsed = lessonId ? parseInt(lessonId) : undefined;
        return Number.isNaN(parsed) ? undefined : parsed;
    });

    const [displayedLesson, setDisplayedLesson] = useState<DetailedLesson>();
    const [projectChanged, setProjectChanged] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [accountVersionStatus, setAccountVersionStatus] = useState<VersionAccountStatus>();
    const [project, setProject] = useState<ProjectCore>({} as ProjectCore);
    const [lessonValidation, setLessonValidation] = useState([false]);
    const [lastLesson, setLastLesson] = useState<number>();

    // Use useMemo to check the value of validatedLesson
    const validatedLesson = useMemo(() => {
        return lessonValidation.every((result: boolean) => result === true);
    }, [lessonValidation]);

    let currentIndex: number = 0;
    if (version && displayedLesson) {
        currentIndex = version.lessons?.findIndex(lesson => lesson.id === displayedLesson.id);
    }

    const versionCount = version?.lessons?.length || 0;

    useEffect(() => {
        const fetchData = async () => {
            if (!formationId || !versionId || !lessonId) {
                return;
            }

            try {
                // If we're on the success page, just get the version data
                if (lessonId === 'success') {
                    const versionData = await getVersion(versionId);
                    const versionStatus = await getVersionAccountStatus(formationId, versionId);

                    setVersion(versionData);
                    setAccountVersionStatus(versionStatus);
                    setShowSuccess(true);

                    if (versionStatus.project_id) {
                        const project = await getProjectInfos(versionStatus.project_id);
                        setProject(project);
                    }
                    return;
                }

                // Regular lesson flow
                const lessonStatus = await checkLessonStatus(formationId, versionId, lessonId);
                setLessonValidation(lessonStatus);

                const versionData = await getVersion(versionId);
                const versionStatus = await getVersionAccountStatus(formationId, versionId);

                setLastLesson(versionData.lessons?.length);
                setVersion(versionData);
                setDisplayedLesson(versionData.lessons.find(lesson => lesson.id === displayedLessonId));
                setAccountVersionStatus(versionStatus);

                if (versionStatus.project_id) {
                    const project = await getProjectInfos(versionStatus.project_id);
                    setProject(project);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formationId, versionId, lessonId, displayedLessonId]);

    // Handle project changes separately
    useEffect(() => {
        async function restart() {
            if (!formationId || !versionId) return;

            try {
                const versionStatus = await getVersionAccountStatus(formationId, versionId);
                setAccountVersionStatus(versionStatus);
                navigate(`/formation/${formationId}/version/${versionId}/lesson/1`);
                window.location.reload();
            } catch (error) {
                console.error('Error handling project change:', error);
            }
        }

        if (projectChanged) {
            restart();
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectChanged, formationId, versionId]);

    function clickNext() {
        if (!version || !displayedLesson) {
            return;
        }
        const currentIndex = version.lessons.findIndex(lesson => lesson.id === displayedLesson.id);

        if (currentIndex < version.lessons.length - 1 && validatedLesson) {
            setDisplayedLesson(version.lessons[currentIndex + 1]);
            setDisplayedLessonId(version.lessons[currentIndex + 1].id);
            navigate(`/formation/${formationId}/version/${versionId}/lesson/${version.lessons[currentIndex + 1].id}`);
        } else {
            setDisplayedLesson({ id: version.lessons[version.lessons.length - 1].id + 1 });
            setShowSuccess(true);
            navigate(`/formation/${formationId}/version/${versionId}/lesson/success`);
        }
    }

    function clickPrevious() {
        if (showSuccess) {
            if (!version || !displayedLesson) {
                return;
            }
            setDisplayedLesson(version.lessons[version.lessons.length - 1]);
            setDisplayedLessonId(version.lessons.length);
            navigate(`/formation/${formationId}/version/${versionId}/lesson/${version.lessons.length}`);
            setShowSuccess(false);
        } else if (version && displayedLesson && displayedLesson.id > version.lessons[version.lessons.length - 1].id) {
            setDisplayedLesson(version.lessons[version.lessons.length - 1]);
            setDisplayedLessonId(version.lessons.length);
            navigate(`/formation/${formationId}/version/${versionId}/lesson/${version.lessons.length}`);
        } else if (displayedLessonId && displayedLessonId > 1) {
            setDisplayedLessonId(displayedLessonId - 1);
            navigate(`/formation/${formationId}/version/${versionId}/lesson/${displayedLessonId - 1}`);
        }
    }

    if (!formationId || !versionId || !lessonId) {
        return <ErrorPage />;
    }

    // Special case for success page
    if (lessonId === 'success') {
        if (!version || !accountVersionStatus) {
            return (
                <div className="flex h-screen w-full items-center justify-center">
                    <TailSpin
                        visible={true}
                        height="30"
                        width="30"
                        color="#293C64"
                        ariaLabel="tail-spin-loading"
                        radius="1"
                        wrapperStyle={{}}
                        wrapperClass="pr-5"
                    />
                </div>
            );
        }

        return (
            <div className="relative flex h-fit min-h-[100vh] items-center overflow-hidden flex-col">
                <div className="fixed z-50 h-24 w-full pointer-events-none">
                    {version && accountVersionStatus && (
                        <VersionProgress
                            version={version}
                            lessons={version.lessons}
                            activeLessonId={version.lessons.length}
                            accountVersionStatus={accountVersionStatus}
                            setDisplayedLessonId={setDisplayedLessonId}
                            setShowSuccess={setShowSuccess}
                            showSuccess={true}
                            navigate={navigate}
                            validatedLesson={true}
                        />
                    )}
                </div>
                <div className="relative z-10 flex w-[100vw] max-w-[1000px] h-full shrink-0 pt-40 lg:pt-36 flex-col items-center px-4 md:w-[80vw] lg:w-[80vw]">
                    {showSuccess === false && currentIndex !== 0 && (
                        <button
                            className="absolute -left-24 top-[420px] flex items-center rounded-full mb-6 justify-center w-12 h-12 bg-white/70 backdrop-blur-lg border border-[#EAECF0] shadow-sm hover:shadow-md hover:cursor-pointer active:shadow-none active:bg-gray-50"
                            onClick={clickPrevious}
                        >
                            <img src={arrowLeft} alt="Back arrow" />
                        </button>
                    )}
                    <VersionSuccess />
                </div>
            </div>
        );
    }

    if (!version || !accountVersionStatus) {
        return (
            <div className="flex h-screen w-full items-center justify-center">
                <TailSpin
                    visible={true}
                    height="30"
                    width="30"
                    color="#293C64"
                    ariaLabel="tail-spin-loading"
                    radius="1"
                    wrapperStyle={{}}
                    wrapperClass="pr-5"
                />
            </div>
        );
    }
    if (!version || !accountVersionStatus) {
        return (
            <div className="flex h-screen w-full items-center justify-center">
                <TailSpin
                    visible={true}
                    height="30"
                    width="30"
                    color="#293C64"
                    ariaLabel="tail-spin-loading"
                    radius="1"
                    wrapperStyle={{}}
                    wrapperClass="pr-5"
                />
            </div>
        );
    }

    return (
        <div className="relative flex h-fit min-h-[100vh] items-center overflow-hidden flex-col">
            <div className="fixed z-50 h-24 w-full pointer-events-none">
                {version && accountVersionStatus && displayedLessonId && (
                    <VersionProgress
                        version={version}
                        lessons={version.lessons}
                        activeLessonId={displayedLessonId}
                        accountVersionStatus={accountVersionStatus}
                        setDisplayedLessonId={setDisplayedLessonId}
                        setShowSuccess={setShowSuccess}
                        showSuccess={showSuccess}
                        navigate={navigate}
                        validatedLesson={validatedLesson}
                    />
                )}
            </div>

            <div className="relative z-10 flex w-[100vw] max-w-[1000px] h-full shrink-0 pt-40 lg:pt-36 flex-col items-center px-4 md:w-[80vw] lg:w-[80vw]">
                <div>
                    {accountVersionStatus &&
                    accountVersionStatus.finished === true &&
                    currentIndex === versionCount - 1 &&
                    !showSuccess ? (
                        <button
                            onClick={() => {
                                setShowSuccess(true);

                                navigate(`/formation/${formationId}/version/${versionId}/lesson/success`);
                            }}
                            className="absolute -right-24 top-[420px] flex items-center rounded-full mb-6 justify-center w-12 h-12 bg-white/30 backdrop-blur-lg border border-[#EAECF0] shadow-sm hover:shadow-md hover:cursor-pointer active:shadow-none active:bg-gray-50"
                        >
                            <img src={diploma} alt="diploma" />
                        </button>
                    ) : !validatedLesson ? (
                        <>
                            <button className="absolute -right-24 top-[420px] flex items-center rounded-full mb-6 justify-center w-12 h-12 bg-white/30 backdrop-blur-lg border border-[#EAECF0] shadow-sm hover:shadow-md hover:cursor-pointer active:shadow-none active:bg-gray-50">
                                <img className="rotate-180 opacity-30" src={arrowLeft} alt="Back arrow" />
                            </button>
                        </>
                    ) : (
                        currentIndex !== versionCount - 1 &&
                        showSuccess === false && (
                            <button
                                className="absolute -right-24 top-[420px] flex items-center rounded-full mb-6 justify-center w-12 h-12 bg-white/30 backdrop-blur-lg border border-[#EAECF0] shadow-sm hover:shadow-md hover:cursor-pointer active:shadow-none active:bg-gray-50"
                                onClick={clickNext}
                            >
                                <img className="rotate-180" src={arrowLeft} alt="Back arrow" />
                            </button>
                        )
                    )}
                </div>
                {currentIndex !== 0 && showSuccess === false && (
                    <button
                        className="absolute -left-24 top-[420px] flex items-center rounded-full mb-6 justify-center w-12 h-12 bg-white/70 backdrop-blur-lg border border-[#EAECF0] shadow-sm hover:shadow-md hover:cursor-pointer active:shadow-none active:bg-gray-50"
                        onClick={clickPrevious}
                    >
                        <img src={arrowLeft} alt="Back arrow" />
                    </button>
                )}
                {version?.formation?.theme && displayedLesson && accountVersionStatus && version?.lessons?.length ? (
                    showSuccess ? (
                        <>
                            <VersionSuccess />
                            <Outlet />
                        </>
                    ) : (
                        <VersionLesson
                            version={version}
                            theme={version?.formation?.theme}
                            lesson={displayedLesson}
                            lessonValidation={lessonValidation}
                            project={project}
                            setShowSuccess={setShowSuccess}
                            setLessonValidation={setLessonValidation}
                            setProjectChanged={setProjectChanged}
                            lastLesson={lastLesson}
                            showSuccess={showSuccess}
                        />
                    )
                ) : null}
            </div>
        </div>
    );
}

export default Version;
